import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import useUserAgent from "@/lib/hooks/useUserAgent";

type PixelPageTransitionProps = {
  token: number | null;
  onCloseComplete?: () => void;
  onComplete?: () => void;
  tileSize?: number;
  pixelColor?: string;
  animationDuration?: number;
  backgroundColor?: string;
  className?: string;
  fadeDuration?: number;
};

type PixelCell = {
  key: string;
  delay: number;
};

type GridDimensions = {
  columns: number;
  rows: number;
};

const MAX_PIXEL_DELAY_FACTOR = 0.5; // Pixels stagger up to 1x animationDuration
const DEFAULT_TILE_SIZE = 48;

export function PixelPageTransition({
  token,
  onCloseComplete,
  onComplete,
  tileSize = DEFAULT_TILE_SIZE,
  pixelColor = "#000000",
  animationDuration = 0.6,
  backgroundColor = "#FFFFFF",
  fadeDuration = 0.2,
  className,
}: PixelPageTransitionProps) {
  const { isIOS } = useUserAgent();

  const safeTileSize = Math.max(tileSize, 1);
  const [status, setStatus] = useState<"idle" | "showing" | "hiding">("idle");
  const [visibleToken, setVisibleToken] = useState<number | null>(null);
  const [phase, setPhase] = useState<"closing" | "opening" | null>(null);
  const phaseStartTimeRef = useRef<number>(0);
  const closingTimeoutRef = useRef<number | null>(null);
  const openingDelayTimeoutRef = useRef<number | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);
  const cleanupTimeoutRef = useRef<number | null>(null);
  const [gridDimensions, setGridDimensions] = useState<GridDimensions>(() => {
    if (typeof window === "undefined") {
      return { columns: 0, rows: 0 };
    }

    const columns = Math.ceil(window.innerWidth / safeTileSize) + 1;
    const rows = Math.ceil(window.innerHeight / safeTileSize) + 1;
    return { columns, rows };
  });
  const revealDurationMs = useMemo(
    () => animationDuration * (1 + MAX_PIXEL_DELAY_FACTOR) * 1000,
    [animationDuration],
  );
  const beginOpeningPhase = useCallback(() => {
    if (phase === "opening") {
      return;
    }

    if (closingTimeoutRef.current) {
      window.clearTimeout(closingTimeoutRef.current);
      closingTimeoutRef.current = null;
    }

    if (openingDelayTimeoutRef.current) {
      window.clearTimeout(openingDelayTimeoutRef.current);
      openingDelayTimeoutRef.current = null;
    }

    if (fadeTimeoutRef.current) {
      window.clearTimeout(fadeTimeoutRef.current);
    }

    if (cleanupTimeoutRef.current) {
      window.clearTimeout(cleanupTimeoutRef.current);
    }

    setPhase("opening");
    setStatus("showing");
    phaseStartTimeRef.current = Date.now();

    fadeTimeoutRef.current = window.setTimeout(() => {
      fadeTimeoutRef.current = null;
      setStatus("hiding");
      cleanupTimeoutRef.current = window.setTimeout(() => {
        setStatus("idle");
        setVisibleToken(null);
        setPhase(null);
        phaseStartTimeRef.current = 0;
        cleanupTimeoutRef.current = null;
        onComplete?.();
      }, fadeDuration * 1000);
    }, revealDurationMs);
  }, [fadeDuration, onComplete, phase, revealDurationMs]);

  useEffect(() => {
    return () => {
      if (closingTimeoutRef.current) {
        window.clearTimeout(closingTimeoutRef.current);
        closingTimeoutRef.current = null;
      }
      if (openingDelayTimeoutRef.current) {
        window.clearTimeout(openingDelayTimeoutRef.current);
        openingDelayTimeoutRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        window.clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (token !== null) {
      if (closingTimeoutRef.current) {
        window.clearTimeout(closingTimeoutRef.current);
        closingTimeoutRef.current = null;
      }
      if (openingDelayTimeoutRef.current) {
        window.clearTimeout(openingDelayTimeoutRef.current);
        openingDelayTimeoutRef.current = null;
      }
      if (fadeTimeoutRef.current) {
        window.clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        window.clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      setVisibleToken(token);
      setPhase("closing");
      setStatus("showing");
      phaseStartTimeRef.current = Date.now();

      closingTimeoutRef.current = window.setTimeout(() => {
        closingTimeoutRef.current = null;
        onCloseComplete?.();
      }, revealDurationMs);

      return;
    }

    if (visibleToken !== null && phase === "closing") {
      if (openingDelayTimeoutRef.current) {
        return;
      }

      const elapsedMs = Date.now() - phaseStartTimeRef.current;
      const waitBeforeOpening = Math.max(0, revealDurationMs - elapsedMs);

      openingDelayTimeoutRef.current = window.setTimeout(() => {
        openingDelayTimeoutRef.current = null;
        beginOpeningPhase();
      }, waitBeforeOpening);
    }
  }, [
    token,
    visibleToken,
    phase,
    revealDurationMs,
    beginOpeningPhase,
    onCloseComplete,
  ]);

  useEffect(() => {
    if (phase === null) {
      return;
    }

    const updateGridDimensions = () => {
      if (typeof window === "undefined") {
        return;
      }

      const columns = Math.ceil(window.innerWidth / safeTileSize) + 1;
      const rows = Math.ceil(window.innerHeight / safeTileSize) + 1;
      setGridDimensions({ columns, rows });
    };

    updateGridDimensions();
    window.addEventListener("resize", updateGridDimensions);

    return () => {
      window.removeEventListener("resize", updateGridDimensions);
    };
  }, [phase, safeTileSize]);

  const cells: PixelCell[] = useMemo(() => {
    if (
      visibleToken === null ||
      gridDimensions.columns === 0 ||
      gridDimensions.rows === 0
    ) {
      return [];
    }

    const total = gridDimensions.columns * gridDimensions.rows;
    const seededRandom = (seed: number) => {
      const value = Math.sin(seed) * 10000;
      return value - Math.floor(value);
    };

    return Array.from({ length: total }, (_, index) => {
      const base = seededRandom(visibleToken * 13.37 + index * 3.7);
      const variance = seededRandom(visibleToken * 7.31 + total + index * 1.17);
      const delay = base * 0.7 + variance * 0.3;

      return {
        key: `${visibleToken}-${total}-${index}`,
        delay,
      };
    });
  }, [gridDimensions, visibleToken]);

  if (
    status === "idle" ||
    phase === null ||
    visibleToken === null ||
    gridDimensions.columns === 0 ||
    gridDimensions.rows === 0
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "pixel-page-transition fixed inset-0 z-[999] overflow-hidden",
        status === "showing" && "pixel-page-transition--showing",
        status === "hiding" && "pixel-page-transition--hiding",
        className,
      )}
      style={
        {
          backgroundColor,
          "--pixel-fade-duration": `${fadeDuration}s`,
        } as CSSProperties & { "--pixel-fade-duration"?: string }
      }
    >
      <div
        className="pixel-page-transition__grid"
        style={{
          gridTemplateColumns: `repeat(${gridDimensions.columns}, ${safeTileSize}px)`,
          gridTemplateRows: `repeat(${gridDimensions.rows}, ${safeTileSize}px)`,
          width: `${gridDimensions.columns * safeTileSize}px`,
          height: `${gridDimensions.rows * safeTileSize}px`,
        }}
      >
        {cells.map((cell) => (
          <div
            key={`${cell.key}-${phase}`}
            className="pixel-page-transition__pixel"
            data-phase={phase}
            style={
              {
                backgroundColor: pixelColor,
                "--pixel-duration": `${animationDuration}s`,
                "--pixel-delay": `${cell.delay * animationDuration}s`,
              } as CSSProperties & {
                "--pixel-duration"?: string;
                "--pixel-delay"?: string;
              }
            }
          />
        ))}
      </div>
    </div>
  );
}
